import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
    X,
    Star,
    MapPin,
    Calendar,
    Clock,
    Sparkle,
    Suitcase,
    Heart,
    ShieldCheck,
    CaretLeft,
    CaretRight,
    ArrowSquareOut,
    Spinner,
    ForkKnife,
} from "@phosphor-icons/react";
import { toAbsolute, reviewsApi, conciergeApi, travelDateApi } from "@/lib/apiClient";
import { safetyColor } from "@/components/ReviewsPanel";
import { useAuth } from "@/context/AuthContext";
import { PASSPORT_DESTINATION_PLACEHOLDER } from "@/constants/destinationImage";

function formatBudget(budget) {
    if (!budget) return null;
    if (typeof budget === "string" || typeof budget === "number") return String(budget);
    if (typeof budget === "object") {
        if (budget.total != null) {
            const sym = budget.currency === "EUR" ? "€" : budget.currency === "GBP" ? "£" : "$";
            return `${sym}${Number(budget.total).toLocaleString()} ${budget.currency || ""}`.trim();
        }
        if (budget.amount != null) return `$${Number(budget.amount).toLocaleString()}`;
    }
    return null;
}

function formatText(val) {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object") return val.name || val.title || val.label || "";
    return String(val);
}

export default function SharedPassportDetailModal({ preview, onClose }) {
    const { user } = useAuth();
    const [detailData, setDetailData] = useState(preview?.data || null);
    const [loading, setLoading] = useState(!preview?.data);
    const [activePhotoIdx, setActivePhotoIdx] = useState(0);

    const entityType = preview?.entity_type;
    const entityId = preview?.entity_id;

    // Fallback load if preview.data was not initially present
    useEffect(() => {
        if (preview?.data) {
            setDetailData(preview.data);
            setLoading(false);
            return;
        }
        if (!entityType || !entityId) return;

        let active = true;
        setLoading(true);

        const fetchFull = async () => {
            try {
                if (entityType === "review") {
                    if (typeof reviewsApi.get === "function") {
                        const res = await reviewsApi.get(entityId);
                        if (active) setDetailData(res);
                    }
                } else if (entityType === "trip") {
                    const res = await conciergeApi.getTrip(entityId);
                    if (active) setDetailData(res);
                } else if (entityType === "traveldate") {
                    const res = await travelDateApi.get(entityId);
                    if (active) setDetailData(res);
                }
            } catch (err) {
                // Keep preview basic data as fallback
                if (active) setDetailData(null);
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchFull();
        return () => {
            active = false;
        };
    }, [preview, entityType, entityId]);

    // Handle Escape key
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    if (!preview) return null;

    const data = detailData || {};
    const photos = data.photos?.length ? data.photos : (preview.image_url ? [preview.image_url] : []);

    const nextPhoto = (e) => {
        e.stopPropagation();
        setActivePhotoIdx((prev) => (prev + 1) % photos.length);
    };

    const prevPhoto = (e) => {
        e.stopPropagation();
        setActivePhotoIdx((prev) => (prev - 1 + photos.length) % photos.length);
    };

    return ReactDOM.createPortal(
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-5"
                style={{ background: "rgba(5, 10, 17, 0.88)", backdropFilter: "blur(12px)" }}
                onClick={onClose}
                data-testid="shared-passport-detail-backdrop"
            >
                <motion.div
                    initial={{ scale: 0.94, opacity: 0, y: 15 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.94, opacity: 0, y: 15 }}
                    transition={{ type: "spring", stiffness: 380, damping: 28 }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative w-full max-w-2xl max-h-[88vh] flex flex-col rounded-[28px] overflow-hidden shadow-2xl border"
                    style={{
                        background: "rgba(9, 15, 26, 0.98)",
                        borderColor: "rgba(212, 175, 55, 0.28)",
                        boxShadow: "0 25px 60px -15px rgba(0, 0, 0, 0.8), 0 0 35px rgba(212, 175, 55, 0.1)",
                    }}
                    data-testid="shared-passport-detail-modal"
                >
                    {/* Header bar */}
                    <div className="flex items-center justify-between px-6 py-4 border-b shrink-0" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
                        <div className="flex items-center gap-2.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-[#D4AF37] animate-pulse" />
                            <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#D4AF37]">
                                Passport {entityType}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-8 h-8 rounded-full grid place-items-center bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors"
                            data-testid="shared-passport-detail-close"
                        >
                            <X size={16} />
                        </button>
                    </div>

                    {/* Scrollable content body */}
                    <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6">
                        {loading ? (
                            <div className="py-20 text-center text-white/50">
                                <Spinner size={28} className="animate-spin inline-block mb-3 text-[#D4AF37]" />
                                <div className="text-sm">Loading full details…</div>
                            </div>
                        ) : entityType === "review" ? (
                            /* ─── REVIEW DETAIL ────────────────────────────────────────────────────────── */
                            <div className="space-y-5" data-testid="shared-review-content">
                                {photos.length > 0 && (
                                    <div className="relative aspect-[16/9] max-h-72 rounded-2xl overflow-hidden bg-black/60 group">
                                        <img
                                            src={toAbsolute(photos[activePhotoIdx])}
                                            alt={data.place_name || preview.title}
                                            className="w-full h-full object-cover"
                                        />
                                        {photos.length > 1 && (
                                            <>
                                                <button
                                                    onClick={prevPhoto}
                                                    type="button"
                                                    className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                                                >
                                                    <CaretLeft size={16} />
                                                </button>
                                                <button
                                                    onClick={nextPhoto}
                                                    type="button"
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                                                >
                                                    <CaretRight size={16} />
                                                </button>
                                                <div className="absolute bottom-3 right-3 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-black/70 text-[#D4AF37] border border-[#D4AF37]/30">
                                                    {activePhotoIdx + 1} / {photos.length}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wider text-white/50 font-medium">
                                            {data.category || "Place"} · {data.city || preview.subtitle}, {data.country}
                                        </div>
                                        <h2 className="font-serif text-2xl sm:text-3xl text-white font-medium mt-1">
                                            {data.place_name || preview.title}
                                        </h2>
                                    </div>
                                    <div className="flex items-center gap-1 bg-[#D4AF37]/10 px-3 py-1.5 rounded-full border border-[#D4AF37]/30 shrink-0">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <Star
                                                key={i}
                                                size={14}
                                                weight={i < (data.rating || 5) ? "fill" : "regular"}
                                                color={i < (data.rating || 5) ? "#D4AF37" : "rgba(255,255,255,0.3)"}
                                            />
                                        ))}
                                        <span className="text-xs font-semibold text-[#D4AF37] ml-1">{data.rating || 5}.0</span>
                                    </div>
                                </div>

                                {data.tip && (
                                    <div className="rounded-2xl p-4 sm:p-5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/80 mb-2 font-semibold">Traveler Tip & Review</div>
                                        <p className="text-white/85 text-sm sm:text-base leading-relaxed whitespace-pre-line">
                                            {data.tip}
                                        </p>
                                    </div>
                                )}

                                {/* Metadata badges */}
                                <div className="flex flex-wrap items-center gap-2.5 pt-1">
                                    {data.pricing && (
                                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/5 border border-white/10 text-[#D4AF37]">
                                            Pricing: {data.pricing}
                                        </span>
                                    )}
                                    {data.accessibility && (
                                        <span className="px-3 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-white/70">
                                            ♿ {data.accessibility}
                                        </span>
                                    )}
                                    {data.safety && (
                                        <span
                                            className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 border"
                                            style={{
                                                borderColor: `${safetyColor(data.safety)}44`,
                                                background: `${safetyColor(data.safety)}18`,
                                                color: safetyColor(data.safety),
                                            }}
                                        >
                                            <ShieldCheck size={14} />
                                            {data.safety}
                                        </span>
                                    )}
                                </div>

                                {/* Author attribution */}
                                {(data.user_handle || preview.subtitle?.startsWith("@")) && (
                                    <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                                        <div className="flex items-center gap-2.5">
                                            {data.user_avatar && (
                                                <img src={toAbsolute(data.user_avatar)} alt="" className="w-8 h-8 rounded-full object-cover" />
                                            )}
                                            <span className="text-sm font-medium text-white/80">
                                                Review by <span className="text-[#D4AF37]">@{data.user_handle || preview.subtitle?.replace("@", "")}</span>
                                            </span>
                                        </div>
                                        <Link
                                            to={preview.path || `/profile/${data.user_handle || preview.subtitle?.replace("@", "")}`}
                                            onClick={onClose}
                                            className="btn-gold text-xs !py-1.5 !px-3 inline-flex items-center gap-1.5"
                                        >
                                            View Traveler <ArrowSquareOut size={13} />
                                        </Link>
                                    </div>
                                )}
                            </div>
                        ) : entityType === "trip" ? (
                            /* ─── TRIP DETAIL ──────────────────────────────────────────────────────────── */
                            <div className="space-y-5" data-testid="shared-trip-content">
                                <div className="relative aspect-[16/8] max-h-64 rounded-2xl overflow-hidden bg-black/60">
                                    <img
                                        src={toAbsolute(data.hero_image || preview.image_url || PASSPORT_DESTINATION_PLACEHOLDER)}
                                        alt={data.title || preview.title}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                                    <div className="absolute bottom-4 left-4 right-4">
                                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
                                            <Suitcase size={14} />
                                            {data.status?.replace("_", " ") || "Itinerary"} · {data.days ? `${data.days} Days` : "Custom Trip"}
                                        </div>
                                        <h2 className="font-serif text-2xl sm:text-3xl text-white font-medium mt-1">
                                            {data.title || preview.title}
                                        </h2>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-2 text-xs">
                                    {data.destination && (
                                        <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/80 flex items-center gap-1.5">
                                            <MapPin size={13} color="#D4AF37" /> {formatText(data.destination)}
                                        </span>
                                    )}
                                    {data.style && (
                                        <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/80">
                                            Style: {formatText(data.style)}
                                        </span>
                                    )}
                                    {formatBudget(data.budget) && (
                                        <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/80">
                                            Budget: {formatBudget(data.budget)}
                                        </span>
                                    )}
                                </div>

                                {/* Itinerary preview / Days breakdown */}
                                {Array.isArray(data.itinerary || data.days_plan) && (data.itinerary || data.days_plan).length > 0 && (
                                    <div className="space-y-3">
                                        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
                                            Trip Schedule Overview
                                        </div>
                                        <div className="space-y-2">
                                            {(data.itinerary || data.days_plan).slice(0, 5).map((dItem, idx) => (
                                                <div
                                                    key={idx}
                                                    className="p-3.5 rounded-xl border text-sm"
                                                    style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.06)" }}
                                                >
                                                    <div className="font-semibold text-[#D4AF37] mb-1">
                                                        Day {idx + 1}: {formatText(dItem.title || dItem.theme) || `Day ${idx + 1}`}
                                                    </div>
                                                    <p className="text-white/70 text-xs leading-relaxed line-clamp-2">
                                                        {formatText(dItem.summary || dItem.description) || (Array.isArray(dItem.activities) ? dItem.activities.map(formatText).filter(Boolean).join(" · ") : "Activities planned")}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="pt-2 text-right">
                                    <Link
                                        to={data.user_id && user?.id && data.user_id === user.id ? `/trip/${entityId}` : (data.share_slug ? `/trip/share/${data.share_slug}` : (preview.path || `/trip/${entityId}`))}
                                        onClick={onClose}
                                        className="btn-gold text-xs !py-2 !px-4 inline-flex items-center gap-1.5"
                                        data-testid="shared-trip-open-full"
                                    >
                                        {data.user_id && user?.id && data.user_id === user.id ? "Open Trip Dashboard" : "Open Shared Trip"} <ArrowSquareOut size={14} />
                                    </Link>
                                </div>
                            </div>
                        ) : entityType === "traveldate" ? (
                            /* ─── TRAVELDATE DETAIL ─────────────────────────────────────────────────────── */
                            <div className="space-y-5" data-testid="shared-traveldate-content">
                                <div className="relative w-full h-48 rounded-2xl overflow-hidden bg-black/60">
                                    <img
                                        src={toAbsolute(data.hero_image || preview.image_url || PASSPORT_DESTINATION_PLACEHOLDER)}
                                        alt={data.title || preview.title}
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                                    <div className="absolute bottom-3 left-4 right-4">
                                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F4A28F]">
                                            <Heart size={14} weight="fill" />
                                            TravelDate · {data.style || "Romantic"} · {data.time_budget || "Date Plan"}
                                        </div>
                                        <h2 className="font-serif text-xl sm:text-2xl text-white font-medium mt-1 line-clamp-1">
                                            {data.title || preview.title}
                                        </h2>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-2 text-xs">
                                    {data.city && (
                                        <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/80 flex items-center gap-1.5">
                                            <MapPin size={13} color="#F4A28F" /> {data.city}, {data.country}
                                        </span>
                                    )}
                                    {data.vibe && (
                                        <span className="px-3 py-1 rounded-full bg-[#F4A28F]/10 border border-[#F4A28F]/25 text-[#F4A28F] flex items-center gap-1">
                                            <Sparkle size={13} /> {data.vibe}
                                        </span>
                                    )}
                                </div>

                                {/* Stops timeline preview */}
                                {Array.isArray(data.stops || data.itinerary) && (data.stops || data.itinerary).length > 0 && (
                                    <div className="space-y-3">
                                        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F4A28F]">
                                            Curated Date Stops
                                        </div>
                                        <div className="space-y-2">
                                            {(data.stops || data.itinerary).slice(0, 4).map((stop, idx) => (
                                                <div
                                                    key={idx}
                                                    className="px-3.5 py-3 rounded-xl border flex items-start gap-3"
                                                    style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.06)" }}
                                                >
                                                    <div className="w-5 h-5 rounded-full bg-[#F4A28F]/20 text-[#F4A28F] text-[11px] font-bold grid place-items-center shrink-0 mt-0.5">
                                                        {idx + 1}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-semibold text-white text-sm truncate">
                                                            {stop.name || stop.place || stop.title || `Stop ${idx + 1}`}
                                                            {stop.time && <span className="text-white/45 text-xs font-normal ml-2">({stop.time})</span>}
                                                        </div>
                                                        <p className="text-white/60 text-xs mt-0.5 leading-relaxed line-clamp-2">
                                                            {stop.note || stop.description || stop.activity || "A curated stop on your route"}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="pt-2 text-right">
                                    <Link
                                        to={data.user_id && user?.id && data.user_id === user.id ? `/traveldate/${entityId}` : (data.share_slug ? `/traveldate/share/${data.share_slug}` : (preview.path || `/traveldate/${entityId}`))}
                                        onClick={onClose}
                                        className="btn-gold text-xs !py-2 !px-4 inline-flex items-center gap-1.5"
                                        data-testid="shared-traveldate-open-full"
                                    >
                                        {data.user_id && user?.id && data.user_id === user.id ? "Open TravelDate" : "Open Shared Date"} <ArrowSquareOut size={14} />
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            /* Generic Fallback */
                            <div className="space-y-4 text-center py-8">
                                <h3 className="font-serif text-2xl text-white">{preview.title}</h3>
                                {preview.subtitle && <p className="text-white/60 text-sm">{preview.subtitle}</p>}
                                {preview.path && (
                                    <Link to={preview.path} onClick={onClose} className="btn-gold text-xs !py-2 !px-4 inline-flex items-center gap-1.5">
                                        View on Passport <ArrowSquareOut size={14} />
                                    </Link>
                                )}
                            </div>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>,
        document.body
    );
}
